import React, { useState, useMemo } from 'react';
import { HeaderBanner } from '@husslehq/api-client-ts';

import { track } from '../../services/analytics';

import { HeaderContext, MenuItem } from './HeaderContext';

import { HeaderActions } from './HeaderActions/HeaderActions';
import { DropDown } from '../DropDown/DropDown';
import { MainMenu } from './MainMenu/MainMenu';
import { Wallet } from './Wallet/Wallet';
import { MobileLoginActions } from './MobileLoginActions/MobileLoginActions';
import { LogoFull } from '../Icons';
import { Grid, PromoBanner, Row, SrOnly } from '@husslehq/ui-library';
import { promotionIsDismissed, promotionMarkAsDismissed } from '../../services/promotion';

import styles from './Header.module.scss';

export type HeaderProps = {
  isMobile: boolean;
  basketCount: number;
  basketAmount?: string;
  walletAmount: number;
  hasGift: boolean;
  isLoggedIn: boolean;
  feedback: number;
  headerBanner?: HeaderBanner;
};

export const Header: React.FC<HeaderProps> = ({
  isMobile,
  basketCount,
  basketAmount,
  walletAmount,
  hasGift,
  isLoggedIn,
  feedback,
  headerBanner,
}) => {
  const [isDropDownOpen, setDropDownVisibility] = useState(false);
  const [isWalletDropDownOpen, setWalletVisibility] = useState(false);
  const [isPromotionVisible, setPromotionVisibility] = useState(true);

  const setNavigationVisibility = (showNavigation: boolean) => {
    setDropDownVisibility(showNavigation);
    setWalletVisibility(false);
  };

  const setWalletDropdownVisibility = (showWallet: boolean) => {
    setNavigationVisibility(false);
    setWalletVisibility(showWallet);
  };

  const handleLogout = () => {
    document.location.href = '/logout';
  };

  const handleLogin = () => {
    document.location.href = '/login';
  };

  const handleRegister = () => {
    document.location.href = '/register';
  };

  const menuItems = useMemo(() => {
    const items: Array<MenuItem> = [];

    if (isMobile) {
      if (isLoggedIn) {
        items.push(
          {
            label: 'My passes',
            link: '/account/passes',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
          {
            label: 'Pass types',
            link: '/flexible-gym-day-and-monthly-passes',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
          {
            label: 'Settings',
            link: '/account/personal',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
          {
            label: 'Payment details',
            link: '/account/cards',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
          {
            label: `Feedback${feedback > 0 ? ` (${feedback})` : ''}`,
            link: '/account/feedback',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
          {
            label: 'Favourites',
            link: '/account/favourite-gyms',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
          {
            label: 'FAQs & Contact',
            link: '/help',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
          {
            label: 'Log out',
            action: () => handleLogout(),
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
        );
      }

      if (!isLoggedIn) {
        items.push(
          {
            label: 'Pass types',
            link: '/flexible-gym-day-and-monthly-passes',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
          {
            label: 'FAQs & Contact',
            link: '/help',
            trackingCategory: 'Mobile menu',
            trackingAction: 'OnClick',
            trackingLabel: 'Menu',
          },
        );
      }
    }

    if (!isMobile && isLoggedIn) {
      items.push(
        {
          label: 'My passes',
          link: '/account/passes',
          trackingCategory: 'Search Page',
          trackingAction: 'OnClick',
          trackingLabel: 'Menu My passes',
        },
        {
          label: 'Payment details',
          link: '/account/cards',
          trackingCategory: 'Search Page',
          trackingAction: 'OnClick',
          trackingLabel: 'Menu Payment details',
        },
        {
          label: 'Settings',
          link: '/account/personal',
          trackingCategory: 'Search Page',
          trackingAction: 'OnClick',
          trackingLabel: 'Menu Settings',
        },
        {
          label: 'History',
          link: '/account/history',
          trackingCategory: 'Search Page',
          trackingAction: 'OnClick',
          trackingLabel: 'Menu History',
        },
        {
          label: `Feedback${feedback > 0 ? ` (${feedback})` : ''}`,
          link: '/account/feedback',
          trackingCategory: 'Search Page',
          trackingAction: 'OnClick',
          trackingLabel: 'Menu Feedback',
        },
        {
          label: 'Redeem a voucher',
          link: '/account/vouchers',
          trackingCategory: 'Search Page',
          trackingAction: 'OnClick',
          trackingLabel: 'Menu Redeem a voucher',
        },
        {
          label: 'FAQs & Contact',
          link: '/help',
          trackingCategory: 'Search Page',
          trackingAction: 'OnClick',
          trackingLabel: 'Menu FAQs and Contact',
        },
        {
          label: 'Log out',
          action: handleLogout,
          trackingCategory: 'Search Page',
          trackingAction: 'OnClick',
          trackingLabel: 'Menu Log out',
        },
      );
    }

    return items;
  }, [isLoggedIn, isMobile]);

  return (
    <HeaderContext.Provider
      value={{
        isAuthenticated: isLoggedIn,
        isDropDownOpen,
        isMobileView: isMobile,
        login: handleLogin,
        logout: handleLogout,
        register: handleRegister,
        menuItems,
        toggleDropDown: () => setNavigationVisibility(!isDropDownOpen),
        walletAmount,
        headerBanner,
      }}
    >
      <header className={styles.header}>
        <Grid>
          <Row>
            <div className={styles['header__container']}>
              <a href="/" className={styles['header__logo']}>
                <LogoFull />
                <SrOnly>Hussle - Home page</SrOnly>
              </a>
              <HeaderActions
                basketCount={basketCount}
                basketAmount={basketAmount}
                showGift={basketCount < 1 && hasGift}
                walletAction={() => setWalletDropdownVisibility(!isWalletDropDownOpen)}
              />
            </div>
          </Row>
        </Grid>
        {headerBanner && isPromotionVisible && !promotionIsDismissed(headerBanner) && (
          <PromoBanner
            bannerStyle={headerBanner.style}
            title={headerBanner.title}
            showButton={true}
            buttonMessage={headerBanner.buttonMessage}
            buttonLink={headerBanner.buttonLink}
            terms={headerBanner.termsConditions}
            isHidden={headerBanner.hidden}
            onClose={() => {
              track({
                event: 'gym_spa',
                action: 'On Site Messaging',
                category: 'Close Message',
                label: headerBanner.code,
              });
              setPromotionVisibility(false);
              promotionMarkAsDismissed(headerBanner);
            }}
            onButtonClick={() => {
              track({
                event: 'gym_spa',
                action: 'On Site Messaging',
                category: 'Accept Message',
                label: headerBanner.code,
              });
            }}
          />
        )}
        {isMobile && (
          <>
            <DropDown isOpen={isDropDownOpen} shouldSlideDown={isMobile}>
              {!isLoggedIn && <MobileLoginActions />}
              <MainMenu />
            </DropDown>
            <DropDown isOpen={isWalletDropDownOpen} shouldSlideDown={isMobile}>
              <Wallet action="/account/voucher" referralUrl="/refer-a-friend" />
            </DropDown>
          </>
        )}
      </header>
    </HeaderContext.Provider>
  );
};
